import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { TOAST_MESSAGE } from 'constants/helperText';
import { StatusCode } from 'enums/StatusCode';
import { IErrorResponse } from 'interfaces/Common/IErrorResponse';
import { IACSJobDetails } from 'interfaces/Jobs/IACSJobDetails';
import { IACSJobRequest } from 'interfaces/Jobs/IACSJobRequest';
import { IJobDetailRequest } from 'interfaces/Jobs/IJobDetailRequest';
import { trackPromise } from 'react-promise-tracker';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import * as service from 'services/jobs/jobService';
import { trackEvent } from 'services/logging/appInsights';
import { RootState } from 'store/configureStore';
import { openAlert } from 'store/slices/alertbar/alertbarSlice';
import { setError } from 'store/slices/error/errorSlice';
import { cognitiveJobActions } from 'store/slices/jobs/cognitiveJobSearch/cognitiveJobSlice';
import { ICognitiveJobState } from 'store/slices/jobs/cognitiveJobSearch/cognitiveJobState';

const trackedJobDetailsData = (fn, ...args) =>
  trackPromise(fn(...args), 'get-job-details');

const trackedJobsData = (fn, ...args) =>
  trackPromise(fn(...args), 'get-jobs-cognitive');

const userId = (state: RootState) => state.auth.userId;
const fetchedJobs = (state: RootState) => state.jobsV2;

export function* fetchACSJobsSaga(action: PayloadAction<IACSJobRequest>) {
  let currentUserId = yield select(userId);
  let fetchedJobResult = yield select(fetchedJobs);

  const emptyResponse: ICognitiveJobState = {
    selectedJobDetails: {} as IACSJobDetails,
    jobCount: 0,
    jobs: [],
    filters: [],
    isLoading: false,
  };

  if (!currentUserId) return;

  if (!action?.payload?.userId) {
    action.payload.userId = currentUserId;
  }

  yield put(cognitiveJobActions.setRequestFilter(action?.payload));
  try {
    const response = yield call(
      trackedJobsData,
      service.fetchJobsV2,
      action.payload,
    );

    // Trigger anytime we get jobs on the jobs page
    trackEvent('JobSearchExecuted');

    const { data, status } = response;
    switch (status) {
      case StatusCode.NoContent:
        yield put(cognitiveJobActions.setJobsACS(emptyResponse));
        break;
      case StatusCode.OK:
        let jobs = data;
        console.log('jobs', jobs, fetchedJobResult.jobs);

        if (+(action?.payload?.pageNumber ?? '0') > 1) {
          yield put(
            cognitiveJobActions.setJobsACS({
              ...jobs,
              jobs: [...fetchedJobResult.jobs, ...jobs.jobs],
            }),
          );
        } else {
          yield put(
            cognitiveJobActions.setJobsACS({
              ...jobs,
            }),
          );
        }
        break;
      default:
        yield put(cognitiveJobActions.setJobsACS(emptyResponse));
    }
  } catch (error: any) {
    let err = error.data as IErrorResponse;
    err.errorMessage = TOAST_MESSAGE.SomethingWentWrongTryReloading;
    yield put(setError(err));
    yield put(
      openAlert({
        variant: 'error',
        message: TOAST_MESSAGE.SomethingWentWrongTryReloading,
      }),
    );
  }
}

function* fetchSelectedJobDetails(action: PayloadAction<number | undefined>) {
  yield put(cognitiveJobActions.setLoadingState(true));
  if (action.payload === undefined) {
    yield put(cognitiveJobActions.resetSelectedJobDetails());
    yield put(cognitiveJobActions.setLoadingState(false));
    return;
  }
  let currentUserId = yield select(userId);
  let jobsRequest = {} as IJobDetailRequest;
  jobsRequest.userId = currentUserId;
  jobsRequest.jobId = action.payload;

  try {
    const { data } = yield call(service.fetchJobDetailsV2, jobsRequest);
    if (data) {
      yield put(cognitiveJobActions.setSelectedJobDetails(data));
    }
  } catch (e) {
    console.log(e);
    yield put(
      openAlert({
        variant: 'error',
        message: TOAST_MESSAGE.SomethingWentWrongTryReloading,
      }),
    );
  } finally {
    yield put(cognitiveJobActions.setLoadingState(false));
  }
}

export function* cognitiveJobSaga() {
  yield all([
    takeLatest(cognitiveJobActions.fetchJobs.type, fetchACSJobsSaga),
    takeLatest(
      cognitiveJobActions.getJobDetailsAction.type,
      fetchSelectedJobDetails,
    ),
  ]);
}
